// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
/* eslint-disable @typescript-eslint/no-explicit-any */

import { toast } from 'react-toastify';
import { store } from 'redux/store';
import { AxiosConfig, createAxios } from 'ts/createAxios';
import { getApiPath } from 'util/commons';
import { API_ENDPOINTS } from 'util/endPoints';
import { handleError } from 'util/errorHandler';
import {
  Area,
  AreaDetail,
  CollectDelivery,
  CollectDeliveryResponse,
} from 'util/Types';

export const getCollectAreas = async (params?: {
  keyword?: string;
  routeId?: number | string;
  successCallback?: (data: Area[]) => void;
  failedCallback?: (error: any) => void;
  finallyCallback?: () => void;
  axiosConfigs?: AxiosConfig;
  showError?: boolean;
}) => {
  const {
    keyword,
    routeId,
    successCallback,
    failedCallback,
    finallyCallback,
    axiosConfigs,
    showError = true,
  } = params || {};

  try {
    const { data } = await createAxios(axiosConfigs).get<Area[]>(
      API_ENDPOINTS.COLLECT_AREAS,
      {
        params: {
          keyword,
          routeId:
            routeId && routeId !== 'none' && routeId !== 'all'
              ? Number(routeId)
              : undefined,
        },
      }
    );

    successCallback?.(data);
  } catch (e) {
    if (showError) {
      handleError(e);
    }

    failedCallback?.(e);
  } finally {
    finallyCallback?.();
  }
};

export const getCollectAreaDetail = async (params: {
  areaId: string;
  routeId?: number;
  successCallback?: (data: AreaDetail) => void;
  failedCallback?: (error: any) => void;
  finallyCallback?: () => void;
}) => {
  const { areaId, routeId, successCallback, failedCallback, finallyCallback } =
    params;

  try {
    const { data } = await createAxios({
      prefix: 'v2',
    }).get<AreaDetail>(
      getApiPath(API_ENDPOINTS.COLLECT_AREA_DETAIL, { id: areaId }),
      {
        params: {
          ...(routeId && { routeId }),
        },
      }
    );

    successCallback?.(data);
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  } finally {
    finallyCallback?.();
  }
};

export const getCollectDeliveries = async (params: {
  deliveryIds: number[];
  cursor: string | null;
  take?: number;
  successCallback?: (data: CollectDelivery[]) => void;
  failedCallback?: (error: any) => void;
  getAll?: boolean;
  showLoading?: boolean;
  type?: 'get' | 'globalExport';
}) => {
  const {
    deliveryIds,
    cursor,
    take = 500,
    successCallback,
    failedCallback,
    getAll = true,
    showLoading = true,
    type = 'get',
  } = params;

  try {
    const {
      data: { nextCursor, deliveryDetails },
    } = await createAxios({
      prefix: 'v2',
      notShowLoading: !showLoading,
    }).post<CollectDeliveryResponse>(
      type === 'get'
        ? API_ENDPOINTS.COLLECT_DELIVERIES
        : API_ENDPOINTS.COLLECT_STREAM_DELIVERIES,
      type === 'get'
        ? {
            deliveryIds,
            cursor,
            take,
          }
        : {
            deliveryIds,
          }
    );

    if (
      getAll &&
      nextCursor &&
      (type !== 'globalExport' ||
        store.getState().historiesCsvExport.isExporting)
    ) {
      await getCollectDeliveries({
        deliveryIds,
        cursor: nextCursor,
        take,
        successCallback: (data) => {
          successCallback?.([...deliveryDetails, ...data]);
        },
        failedCallback,
        getAll,
        showLoading,
        type,
      });
    } else if (
      type !== 'globalExport' ||
      store.getState().historiesCsvExport.isExporting
    ) {
      successCallback?.(deliveryDetails);
    }
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  }
};

export const deliveries = async (params: {
  deliveryIds: number[];
  successCallback?: () => void;
  failedCallback?: (error: any) => void;
}) => {
  const { deliveryIds, successCallback, failedCallback } = params;

  try {
    await createAxios().post(API_ENDPOINTS.DELIVERIES, { deliveryIds });

    successCallback?.();
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  }
};

export const updateCollectData = async (params: {
  id: number;
  receivedDatetime: string;
  successCallback?: () => void;
  failedCallback?: (error: any) => void;
}) => {
  const { id, receivedDatetime, successCallback, failedCallback } = params;

  try {
    await createAxios().put(getApiPath(API_ENDPOINTS.COLLECT_DETAIL, { id }), {
      receivedDatetime,
    });

    successCallback?.();
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  }
};

export const deleteCollectsData = async (params: {
  deliveryIds: number[];
  successCallback?: () => void;
  failedCallback?: (error: any) => void;
}) => {
  const { deliveryIds, successCallback, failedCallback } = params;

  try {
    const { data } = await createAxios().delete(API_ENDPOINTS.COLLECTS, {
      data: { deliveryIds },
    });

    toast.success(data);
    successCallback?.();
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  }
};

export const getWasteUnits = async (params: {
  wasteUnitId: number;
  successCallback?: (data: any) => void;
  failedCallback?: (error: any) => void;
}) => {
  const { wasteUnitId, successCallback, failedCallback } = params;

  try {
    const { data } = await createAxios().get(
      getApiPath(API_ENDPOINTS.WASTE_UNITS_ITEMS, {
        id: wasteUnitId.toString(),
      })
    );

    successCallback?.(data);
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  }
};

export const createCollectSubstituteRegist = async (params: {
  body: {
    routeId?: number;
    siteId: string;
    tenantId?: number;
    waste: {
      wasteUnitId: number;
      quantity: string;
    }[];
  };
  successCallback?: () => void;
  failedCallback?: (error: any) => void;
}) => {
  const { body, successCallback, failedCallback } = params;

  try {
    await createAxios({ prefix: 'v2' }).post(
      API_ENDPOINTS.COLLECT_SUBSTITUTE_REGIST,
      body
    );

    successCallback?.();
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  }
};
